import { useCallback, useEffect, useMemo, useState } from 'react';
import { Mic, MicOff, PhoneCall, PhoneOff, PhoneIncoming, Loader2, AlertTriangle, Volume2, VolumeX } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { phoneNumbersAPI, repPhoneAPI } from '../services/api.js';
import { useRepPhone } from '../hooks/useRepPhone.js';

// ── REP PHONE — the sales rep's handset ──────────────────────────────────────
//
// Brian, 2026-09-01: "This number is for a sales representative to use directly …
// they will use the tenant in the dashboard and dial out using a dial interface
// that's on the dashboard. They're not using their mobile device."
//
// So this page IS the phone. The rep's microphone is published straight into the
// LiveKit room the call runs in; the AI goes silent while they are audible and
// picks the line back up only on the modes where that is right (an inbound caller
// whose rep dropped — never a call the rep placed).
//
// ⛔ EVERY CALL FROM THIS SCREEN REACHES A REAL BUSINESS. The guarantees are
// server-side and none of them is re-implemented here: the number is checked against
// the do-not-call list before anything is dialed, and the call is refused outright
// unless this rep could actually take it (REP_SOFTPHONE_ENABLED + owner + a transport
// whose room exists before the prospect answers). This page must never grow a path
// that dials without going through POST /calls/place.
//
// ⚠️ NO CAMPAIGN IS ATTACHED YET, AND THAT IS A REAL GAP, NOT AN OMISSION TO IGNORE.
// The panel gates CAMPAIGN_GUARDRAILS on campaign_id, NOT on is_outbound — deliberately,
// because gating on is_outbound once put a sales pitch on an 88-year-old's care line. So a
// rep dial placed from here carries NO campaign guardrails. Today that is survivable only
// because the AI is not supposed to speak at all on a rep call and every path that could
// has been guarded. The moment a rep call can fall back to the AI, this needs a campaign
// picker. An earlier version of this comment claimed the campaign leg was already being
// sent; it was not.

// How often to ask whether the line is ringing. Inbound overflow gives the rep a
// ~20s window; 3s means they see it with a lot of room to spare without turning an
// idle dashboard into a request loop.
const LINE_POLL_MS = 3_000;

function digitsOnly(v) {
  return String(v || '').replace(/[^0-9+]/g, '');
}

/** Accept what a person actually types and hand the server strict E.164. */
function toE164(raw) {
  const v = digitsOnly(raw);
  if (!v) return null;
  if (v.startsWith('+')) return /^\+[1-9]\d{6,14}$/.test(v) ? v : null;
  // Bare 10 digits is a US number. This is a US sales line; guessing a country here
  // would be worse than refusing, so nothing else is inferred.
  if (/^\d{10}$/.test(v)) return `+1${v}`;
  if (/^1\d{10}$/.test(v)) return `+${v}`;
  return null;
}

export default function RepPhone() {
  const {
    status, error, call, room, armed,
    outputs, outputId, outputSelectable, speakersArmed, playbackBlocked, audioDiag,
    arm, armSpeakers, resumeAudio, dial, answerLine, hangUp, reset
  } = useRepPhone();
  const [testingSpeakers, setTestingSpeakers] = useState(false);
  // LiveKit's verdict, so the picker only appears where the CALL can honour it.
  const canPickOutput = outputSelectable;
  const onTestSpeakers = useCallback(async (deviceId) => {
    setTestingSpeakers(true);
    try { await armSpeakers(deviceId); } finally { setTestingSpeakers(false); }
  }, [armSpeakers]);
  const [numbers, setNumbers] = useState([]);
  const [lineDid, setLineDid] = useState('');
  // The DID drives the inbound poll; the ID is what actually pins the caller ID on a dial.
  // Tracking only the DID is how "prospects see this number" became a caption with nothing
  // behind it — the server fell through to its oldest-active-row fallback instead.
  const [lineId, setLineId] = useState('');
  const [to, setTo] = useState('');
  // Owner-only override of the 8am–9pm floor, for test calls. The server decides whether
  // this account may use it; the box is just how the person says "I accept the hour".
  const [afterHours, setAfterHours] = useState(false);
  const [incoming, setIncoming] = useState(null);   // { callerNumber } when the line is live
  const [featureOff, setFeatureOff] = useState(false);

  const busy = status === 'dialing' || status === 'connecting';
  const live = status === 'live';

  useEffect(() => {
    let alive = true;
    phoneNumbersAPI.list()
      .then((res) => {
        if (!alive) return;
        const rows = (res?.data || []).filter((n) => n.status === 'active');
        setNumbers(rows);
        setLineDid((cur) => cur || rows[0]?.phoneNumber || '');
        setLineId((cur) => cur || rows[0]?.id || '');
      })
      .catch(() => { /* the dialer still works; only the line picker is affected */ });
    return () => { alive = false; };
  }, []);

  // ── IS THE LINE RINGING? ───────────────────────────────────────────────────
  // Polled rather than pushed because core has no event stream — `useEventStream`
  // is a scaffold against a `/events` endpoint that has never existed. Polling a
  // built, tenant-scoped endpoint beats standing up a push channel to find out
  // whether one number is busy.
  //
  // ⚠️ THIS ONLY SEES CALLS ALREADY ON THE LINE. It is not a substitute for the
  // inbound overflow ringing the browser — until that lands, a rep watching this
  // page sees a call that the AI has probably already answered.
  useEffect(() => {
    if (!lineDid || live || busy || featureOff) return undefined;
    let alive = true;
    let timer = null;
    const tick = async () => {
      try {
        const res = await repPhoneAPI.lineStatus(lineDid);
        if (!alive) return;
        const d = res?.data;
        setIncoming(d?.live ? { callerNumber: d.callerNumber } : null);
      } catch (err) {
        if (!alive) return;
        // 404 = the whole rep surface is switched off server-side. Stop polling and
        // say so once, rather than failing silently every three seconds forever.
        if (err?.status === 404) { setFeatureOff(true); return; }
        setIncoming(null);
      }
      if (alive) timer = setTimeout(tick, LINE_POLL_MS);
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [lineDid, live, busy, featureOff]);

  const parsedTo = useMemo(() => toE164(to), [to]);
  // ⛔ `featureOff` BELONGS IN HERE. Without it the Dial button stays live while the server
  // has rep calling switched off — and the server used to place the call anyway, leaving a
  // real prospect with an AI the rep could not speak over. The server refuses now, but the
  // button should not invite the attempt either.
  // ⛔ AND THE SPEAKERS. A dial the rep cannot hear is a live prospect on a line nobody
  // answers — the 2026-09-03 first-call failure, exactly.
  const canDial = armed && speakersArmed && !!parsedTo && !busy && !live && !featureOff;

  const onDial = useCallback((e) => {
    e.preventDefault();
    if (!canDial) return;
    dial(parsedTo, { fromNumberId: lineId || undefined, afterHoursOverride: afterHours });
    // One dial per tick. An override that stayed on would silently cover the next call too.
    setAfterHours(false);
  }, [canDial, dial, parsedTo, lineId, afterHours]);

  return (
    <div className="px-6 md:px-10 py-8 max-w-2xl">
      <PageHeader
        title="Rep phone"
        subtitle="Call prospects yourself from the sales line — your voice, not the AI's"
      />

      {featureOff && (
        <div className="card mb-6 border-amber-700/50">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm text-stone-300">
              <p className="text-stone-100 font-medium mb-1">Rep calling is switched off on the server.</p>
              <p>
                It stays off until the microphone hand-off has been heard working on a real
                call. Set <code className="text-alfred-gold">REP_SOFTPHONE_ENABLED=1</code> to
                turn it on.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && <div className="mb-4"><ErrorBanner message={error} /></div>}

      {/* ── THE MICROPHONE, ON ITS OWN, BEFORE ANYTHING DIALS ────────────────
          Separated from the dial button deliberately: a permission prompt is free
          now and unrecoverable once a prospect has picked up. */}
      <div className="card mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {armed
              ? <Mic size={18} className="text-emerald-500" />
              : <MicOff size={18} className="text-stone-500" />}
            <div>
              <p className="text-stone-100 font-medium">
                {armed ? 'Microphone ready' : 'Microphone off'}
              </p>
              <p className="text-sm text-stone-400">
                {armed
                  ? 'Your voice goes to the prospect. The AI stays quiet while you talk.'
                  : 'Turn this on before you dial — you cannot grant it mid-call.'}
              </p>
            </div>
          </div>
          {!armed && (
            <button type="button" className="btn-primary" onClick={arm} disabled={status === 'arming'}>
              {status === 'arming' ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
              Turn on
            </button>
          )}
        </div>
      </div>

      {/* ── THE SPEAKERS, SAME RULE: PROVEN BEFORE ANYTHING DIALS ─────────────
          The click on "Test speakers" is what lets the browser play inbound audio at
          all, and hearing the tone is the only proof it reaches the headset. */}
      <div className="card mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {speakersArmed
              ? <Volume2 size={18} className="text-emerald-500" />
              : <VolumeX size={18} className="text-stone-500" />}
            <div>
              <p className="text-stone-100 font-medium">
                {speakersArmed ? 'Speakers ready' : 'Speakers not tested'}
              </p>
              <p className="text-sm text-stone-400">
                {speakersArmed
                  ? 'You heard the tone — the prospect will come through the same way.'
                  : armed
                    ? 'Play a test tone so the browser is allowed to play the call, and so you know which output carries it.'
                    : 'Turn the microphone on first — the browser only names outputs after that.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-primary shrink-0"
            onClick={() => onTestSpeakers(outputId)}
            disabled={!armed || testingSpeakers}
          >
            {testingSpeakers ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} />}
            {speakersArmed ? 'Test again' : 'Test speakers'}
          </button>
        </div>
        {armed && canPickOutput && outputs.length > 1 && (
          <div className="mt-3">
            <label className="field-label" htmlFor="rep-output">Play the call through</label>
            <select
              id="rep-output"
              className="field-input"
              value={outputId}
              onChange={(e) => onTestSpeakers(e.target.value)}
              disabled={testingSpeakers}
            >
              <option value="">System default</option>
              {outputs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <p className="text-xs text-stone-500 mt-2">Changing this plays the tone again through the new output.</p>
          </div>
        )}
        {armed && !canPickOutput && (
          <p className="text-xs text-stone-500 mt-3">
            This browser plays calls through the system default output. Pick the headset in your system sound settings.
          </p>
        )}
      </div>

      {/* ── THE LINE ──────────────────────────────────────────────────────── */}
      {numbers.length > 0 && (
        <div className="card mb-4">
          <label className="field-label" htmlFor="rep-line">Your line</label>
          <select
            id="rep-line"
            className="field-input"
            value={lineId}
            onChange={(e) => {
              const row = numbers.find((n) => n.id === e.target.value);
              setLineId(e.target.value);
              setLineDid(row?.phoneNumber || '');
            }}
            disabled={live}
          >
            {numbers.map((n) => (
              <option key={n.id} value={n.id}>
                {n.phoneNumber}{n.label ? ` — ${n.label}` : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-stone-500 mt-2">
            Prospects see this number, and calls back to it ring here.
          </p>
        </div>
      )}

      {/* ── SOMETHING IS LIVE ON THE LINE ─────────────────────────────────── */}
      {incoming && !live && !busy && (
        <div className="card mb-4 border-emerald-700/60">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <PhoneIncoming size={18} className="text-emerald-500" />
              <div>
                <p className="text-stone-100 font-medium">Call in progress on {lineDid}</p>
                <p className="text-sm text-stone-400">
                  {incoming.callerNumber ? `From ${incoming.callerNumber}. ` : ''}
                  Joining puts you on the line and silences the AI.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-primary"
              onClick={() => answerLine(lineDid)}
              disabled={!armed || !speakersArmed}
            >
              <PhoneCall size={16} /> Join
            </button>
          </div>
        </div>
      )}

      {/* ── DIAL ──────────────────────────────────────────────────────────── */}
      {!live && (
        <form className="card mb-4" onSubmit={onDial}>
          <label className="field-label" htmlFor="rep-to">Call a prospect</label>
          <div className="flex gap-3">
            <input
              id="rep-to"
              className="field-input"
              placeholder="(555) 123-4567"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={busy}
              inputMode="tel"
              autoComplete="off"
            />
            <button type="submit" className="btn-primary shrink-0" disabled={!canDial}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : <PhoneCall size={16} />}
              {status === 'dialing' ? 'Dialing…' : status === 'connecting' ? 'Connecting…' : 'Dial'}
            </button>
          </div>
          {to && !parsedTo && (
            <p className="text-xs text-amber-500 mt-2">
              That does not look like a phone number. Use 10 digits, or +1… for anywhere else.
            </p>
          )}
          {status === 'connecting' && (
            <p className="text-xs text-stone-400 mt-2">
              Getting you onto the call before it is answered — this is normal, and it
              finishes well before their phone stops ringing.
            </p>
          )}
          <label className="flex items-start gap-2 mt-3 text-xs text-stone-400 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={afterHours}
              onChange={(e) => setAfterHours(e.target.checked)}
              disabled={busy}
            />
            <span>
              Override calling hours (owner test call). Calls are normally refused outside
              8am–9pm in the campaign or account timezone. Platform owners only; every use is audited.
            </span>
          </label>
          <p className="text-xs text-stone-500 mt-3">
            Numbers on the do-not-call list are refused before anything is dialed.
          </p>
        </form>
      )}

      {/* ── ON A CALL ─────────────────────────────────────────────────────── */}
      {live && (
        <div className="card border-emerald-700/60">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
              <div>
                <p className="text-stone-100 font-medium">
                  On a call{call?.to ? ` with ${call.to}` : ''}
                </p>
                <p className="text-sm text-stone-400">
                  You are live. The AI is silent for as long as you are on.
                </p>
              </div>
            </div>
            <button type="button" className="btn-primary shrink-0" onClick={hangUp}>
              <PhoneOff size={16} /> Hang up
            </button>
          </div>
          {playbackBlocked && (
            <div className="mt-4 flex items-center justify-between gap-4 rounded-md border border-amber-700/60 bg-amber-950/30 p-3">
              <div className="flex items-center gap-2 text-sm text-amber-200">
                <VolumeX size={16} />
                The browser is holding the call's audio. Tap to hear it.
              </div>
              <button type="button" className="btn-primary shrink-0" onClick={resumeAudio}>
                <Volume2 size={16} /> Tap to hear
              </button>
            </div>
          )}
          {room && <p className="text-xs text-stone-600 mt-3 font-mono">{room}</p>}
          {audioDiag && (
            <p className="text-xs text-stone-500 mt-2 font-mono">
              rx{' '}
              {audioDiag.tracks.length === 0
                ? 'no remote audio tracks yet'
                : audioDiag.tracks.map((t) => (
                  `${t.who.split('_')[0].slice(0, 14)}: ${t.subscribed
                    ? `${t.bytes ?? '?'}B e=${t.energy ?? '?'}${t.paused ? ' PAUSED' : ''}${t.muted ? ' MUTED' : ''}${t.sink ? ` →${t.sink.slice(0, 6)}` : ''}`
                    : 'not subscribed'}`
                )).join(' · ')}
              {audioDiag.canPlayback ? '' : ' · playback BLOCKED'}
            </p>
          )}
        </div>
      )}

      {status === 'ended' && (
        <div className="card">
          <p className="text-stone-100 font-medium mb-1">Call ended</p>
          <p className="text-sm text-stone-400 mb-4">
            The transcript and recording appear on the call record once it has been processed.
          </p>
          <button type="button" className="btn-ghost" onClick={reset}>Back to the dialer</button>
        </div>
      )}
    </div>
  );
}
